// Live site list for the dashboard and My Sites. The server reconciles
// in-flight builds on every GET /api/sites, so polling is all the client
// needs for the cards to move from "building" to "draft"/"live" on their
// own. Polls fast while any build is in flight, slow otherwise so a page
// left open still stays current.

import { useCallback, useEffect, useRef, useState } from "react";
import { api, type Site } from "./api";
import { isSiteBuilding } from "./siteLifecycle";
import { subscribeToSiteChanges } from "./siteEvents";

const BUILDING_POLL_MS = 5_000;
const IDLE_POLL_MS = 30_000;

export function useLiveSites() {
  const [sites, setSites] = useState<Site[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const refresh = useCallback(async () => {
    const currentRequest = ++requestId.current;
    try {
      const result = await api.listSites();
      if (currentRequest !== requestId.current) return;
      setSites(result.sites);
      setError(null);
    } catch (reason) {
      if (currentRequest !== requestId.current) return;
      // Only surface an error when we have nothing to show — a transient
      // poll failure shouldn't blank out a working dashboard.
      setSites((current) => {
        if (current === null) setError(String(reason));
        return current;
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
    const unsubscribe = subscribeToSiteChanges(() => { void refresh(); }, false);
    return () => { unsubscribe(); requestId.current += 1; };
  }, [refresh]);

  const loaded = sites !== null;
  const anyBuilding = sites?.some(isSiteBuilding) ?? false;
  useEffect(() => {
    if (!loaded) return;
    const interval = anyBuilding ? BUILDING_POLL_MS : IDLE_POLL_MS;
    const timer = window.setInterval(() => { void refresh(); }, interval);
    return () => window.clearInterval(timer);
  }, [loaded, anyBuilding, refresh]);

  return { sites, setSites, error, refresh };
}
